// Pull requests list (design/screens/01-pull-requests.html).
import { useEffect, useState } from 'react';
import { REPO } from '../config';
import type { GitHub } from '../github/client';
import { filterPrs, listPullRequests, type PrFilter, type PrSummary } from '../review/api';
import { linkProps } from './router';
import { CommentIcon, ExternalIcon, PrIcon } from './icons';

const STATUS_LABEL: Record<PrSummary['status'], string> = {
  'draft': 'Draft',
  'review-requested': 'Your review requested',
  'changes-requested': 'Changes requested',
  'approved': 'Approved',
  'open': 'Open',
};

function byline(p: PrSummary, viewer: string): string {
  const who = p.author === viewer ? 'you' : p.author;
  const files = `${p.markdownFiles} Markdown ${p.markdownFiles === 1 ? 'file' : 'files'} changed`;
  const approved = p.approvedBy.length ? ` · approved by ${p.approvedBy.join(', ')}` : '';
  return `#${p.number} opened by ${who} · ${files}${approved}`;
}

type State = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; viewer: string; prs: PrSummary[] };

export function PullRequests({ gh }: { gh: GitHub }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<PrFilter | null>(null);

  useEffect(() => {
    let live = true;
    listPullRequests(gh, REPO)
      .then((r) => { if (live) setState({ kind: 'ready', ...r }); })
      .catch((e: unknown) => { if (live) setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }); });
    return () => { live = false; };
  }, [gh]);

  const counts = state.kind === 'ready'
    ? { 'needs-review': filterPrs(state.prs, state.viewer, 'needs-review').length, yours: filterPrs(state.prs, state.viewer, 'yours').length, all: state.prs.length }
    : null;
  // Default to "Needs your review" when there is something to review.
  const active: PrFilter = filter ?? (counts && counts['needs-review'] > 0 ? 'needs-review' : 'all');
  const shown = state.kind === 'ready' ? filterPrs(state.prs, state.viewer, active) : [];

  return (
    <div className="hq-split">
      <main className="hq-split__main">
        <div className="hq-pagehead">
          <div>
            <h1 className="hq-title hq-title--small">Pull requests</h1>
            <p className="hq-lead">Open pull requests that change Markdown files in this repo.</p>
          </div>
        </div>

        <nav className="hq-segmented" aria-label="Filter pull requests">
          {(['needs-review', 'yours', 'all'] as const).map((f) => (
            <button key={f} type="button" aria-pressed={active === f} onClick={() => setFilter(f)}>
              {f === 'needs-review' ? 'Needs your review' : f === 'yours' ? 'Yours' : 'All open'}
              {counts && <span className="hq-count">{counts[f]}</span>}
            </button>
          ))}
        </nav>

        {state.kind === 'loading' && (
          <ul className="hq-prlist" aria-busy="true" aria-label="Loading pull requests">
            {[0, 1, 2].map((i) => <li key={i}><div className="hq-skeleton" style={{ width: '55%', height: 18 }} /><div className="hq-skeleton" style={{ width: '35%' }} /></li>)}
          </ul>
        )}
        {state.kind === 'error' && <p className="hq-error" role="alert">Couldn’t load pull requests: {state.message}</p>}
        {state.kind === 'ready' && shown.length === 0 && (
          <div className="hq-empty">
            <p className="hq-lead">
              {active === 'needs-review' ? 'Nothing needs your review right now.' : active === 'yours' ? 'You have no open pull requests.' : 'There are no open pull requests that change Markdown files.'}
            </p>
            <p className="hq-note">Creating a branch and pull request from Hashquill arrives in a later update.</p>
          </div>
        )}
        {shown.length > 0 && state.kind === 'ready' && (
          <ul className="hq-prlist">
            {shown.map((p) => (
              <li key={p.number}>
                <span className={`hq-pricon hq-pricon--${p.isDraft ? 'draft' : 'open'}`}><PrIcon /></span>
                <div className="hq-prlist__main">
                  <a className="hq-prlist__title" {...linkProps({ name: 'pr', number: p.number })}>{p.title}</a>
                  <span className="hq-note">{byline(p, state.viewer)}</span>
                  <span className="hq-branches"><code>{p.headRef}</code><span aria-hidden="true">→</span><code>{p.baseRef}</code></span>
                </div>
                <div className="hq-prlist__meta">
                  {p.comments > 0 && <span className="hq-note" aria-label={`${p.comments} comments`}><CommentIcon />{p.comments}</span>}
                  <span className={`hq-status hq-status--${p.status}`}>{STATUS_LABEL[p.status]}</span>
                  {p.status === 'approved' && (
                    <a className="hq-note" href={p.url} target="_blank" rel="noopener noreferrer">Merge on GitHub<ExternalIcon /></a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      <aside className="hq-split__aside">
        <section className="hq-explainer">
          <h2>How a change reaches <code>main</code></h2>
          <ol>
            <li><span className="hq-mono">01</span><span><strong>Branch.</strong> Your own copy of the specs to change safely.</span></li>
            <li><span className="hq-mono">02</span><span><strong>Commit.</strong> A named snapshot of your edits.</span></li>
            <li><span className="hq-mono">03</span><span><strong>Push.</strong> Sends your commits to GitHub.</span></li>
            <li><span className="hq-mono">04</span><span><strong>Pull request.</strong> Asks the team to review and merge into <code>main</code>.</span></li>
          </ol>
        </section>
        <a className="hq-note" href={`https://github.com/${REPO.full}/pulls`} target="_blank" rel="noopener noreferrer">
          Open these pull requests on GitHub<ExternalIcon />
        </a>
      </aside>
    </div>
  );
}
